import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { OneQrewCard } from './ui';

/**
 * TopCompetitorsPanel - Zeigt die Top-3-Konkurrenten für ein Keyword an
 * Position: Links auf der Karte (gespiegelt zum Suchvolumen Panel)
 * Migriert auf OneQrew Brand Design
 */
const TopCompetitorsPanel = ({ competitors, keyword }) => {
  const theme = useTheme();
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Nichts anzeigen wenn keine Konkurrenten oder kein Keyword
  if (!competitors || competitors.length === 0 || !keyword) {
    return null;
  }

  const handleMouseEnter = (index, event) => {
    setHoveredIndex(index);
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
  };

  // Ranking-Farben basierend auf Position
  const getRankingColor = (index) => {
    if (index === 0) return theme.palette.error.main;
    if (index === 1) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  return (
    <OneQrewCard
      style={{
        position: 'absolute',
        top: theme.spacing(2.5),
        left: theme.spacing(1.25),
        zIndex: 1000,
        minWidth: '280px',
        maxWidth: '300px',
        overflow: 'visible', // TOOLTIP KANN ÜBER PANEL HINAUSRAGEN
        padding: 0
      }}
    >
      {/* Header */}
      <div style={{
        backgroundColor: theme.palette.grey[50],
        padding: theme.spacing(2, 2.5, 1.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
        borderTopLeftRadius: '32px',
        borderTopRightRadius: '32px'
      }}>
        <div style={{
          fontSize: '16px',
          fontWeight: '700',
          color: theme.palette.text.primary,
          marginBottom: theme.spacing(0.5)
        }}>
          Top 3 Konkurrenten in der Region
        </div>
        <div style={{
          fontSize: '14px',
          color: theme.palette.oneqrew.purple,
          fontWeight: '500'
        }}>
          "{keyword}"
        </div>
      </div>
      
      {/* Konkurrenten Liste */}
      <div style={{
        padding: theme.spacing(2, 2.5, 2.5),
        overflow: 'visible' // TOOLTIPS KÖNNEN ÜBER LISTE HINAUSRAGEN
      }}>
        {competitors.slice(0, 3).map((competitor, index) => (
          <div
            key={competitor.placeId || competitor.name || index}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(1.75),
              marginBottom: index < 2 ? theme.spacing(1.5) : '0',
              padding: theme.spacing(1.5, 1),
              borderRadius: '8px',
              transition: 'all 0.15s ease',
              backgroundColor: hoveredIndex === index
                ? `${theme.palette.oneqrew.purple}15`
                : 'transparent',
              border: hoveredIndex === index
                ? `1px solid ${theme.palette.oneqrew.purple}40`
                : '1px solid transparent',
              position: 'relative',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => handleMouseEnter(index, e)}
            onMouseLeave={handleMouseLeave}
          >
            {/* Ranking Badge */}
            <div style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              backgroundColor: getRankingColor(index),
              color: theme.palette.common.white,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '13px',
              fontWeight: '300',
              flexShrink: 0,
              boxShadow: theme.shadows[2],
              border: `2px solid ${theme.palette.common.white}`
            }}>
              {index + 1}
            </div>
            
            {/* Firmenname */}
            <div
              style={{
                fontSize: '14px',
                fontWeight: '500',
                color: theme.palette.text.primary,
                lineHeight: '1.3',
                flex: 1,
                minWidth: 0,
                position: 'relative'
              }}
            >
              {/* Gekürzter Name */}
              <div style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {competitor.name || 'Unbekanntes Unternehmen'}
              </div>
              
              {/* TOOLTIP - RECHTS VOM PANEL (NICHT ABGESCHNITTEN) */}
              {hoveredIndex === index && (
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: 'calc(100% + 15px)', // RECHTS vom Panel
                    transform: 'translateY(-50%)',
                    backgroundColor: theme.palette.grey[900],
                    color: theme.palette.common.white,
                    padding: theme.spacing(1.25, 1.75),
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '300',
                    whiteSpace: 'nowrap',
                    zIndex: 999999,
                    boxShadow: theme.shadows[8],
                    minWidth: '250px',
                    textAlign: 'left'
                  }}
                >
                  {competitor.name || 'Unbekanntes Unternehmen'}

                  {/* Pfeil nach links (zum Panel) */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '-6px',
                      transform: 'translateY(-50%)',
                      width: 0,
                      height: 0,
                      borderTop: '6px solid transparent',
                      borderBottom: '6px solid transparent',
                      borderRight: `6px solid ${theme.palette.grey[900]}`
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
        
        {/* Fallback wenn weniger als 3 Konkurrenten */}
        {competitors.length < 3 && (
          <div style={{
            marginTop: theme.spacing(1.5),
            fontSize: '12px',
            color: theme.palette.text.secondary,
            textAlign: 'center',
            fontStyle: 'italic'
          }}>
            {competitors.length === 0
              ? 'Keine Konkurrenten gefunden'
              : `Nur ${competitors.length} Konkurrent${competitors.length === 1 ? '' : 'en'} gefunden`
            }
          </div>
        )}
      </div>
    </OneQrewCard>
  );
};

export default TopCompetitorsPanel;